<template>
    <ValidationErrors :show="showErrors"/>
    <Head :title="'Supplier Invoices - ' + project.title"/>
    <div class="w-full mb-6">
        <ProjectHeader :project="project"></ProjectHeader>
        <div class="space-y-6 lg:col-span-9">
            <section aria-labelledby="payment-details-heading">
                <form @submit.prevent="submit">
                    <div class="shadow sm:rounded-md sm:overflow-hidden">
                        <div class="bg-white px-4 p-6 space-y-6">
                            <div class="flex pb-5 border-b border-gray-200 justify-between items-center">
                                <h3 class="text-lg leading-6 font-bold text-gray-900">Supplier Invoices</h3>
                                <div class="flex text-right items-end">
                                    <Button @click="emitter.emit('openLinkProjectSupplierInvoiceSlideout', project)"
                                            class="mr-2 shadow-md inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                        <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                                             fill="none"
                                             viewBox="0 0 24 24"
                                             stroke="currentColor" aria-hidden="true">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                  stroke-width="2"
                                                  d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        Link Existing Supplier Invoice
                                    </Button>
                                    <Button @click="emitter.emit('openSupplierInvoiceSlideout', project)"
                                            class="shadow-md inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                        <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                                             fill="none"
                                             viewBox="0 0 24 24"
                                             stroke="currentColor" aria-hidden="true">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                  stroke-width="2"
                                                  d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        Create New Supplier Invoice
                                    </Button>

                                </div>
                            </div>

                            <div class="flex flex-col mt-3 space-y-3">
                                <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                    <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                        <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                            <table class="min-w-full divide-y divide-gray-200">
                                                <thead class="bg-gray-50">
                                                <tr>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Invoice Number
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Supplier
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Received Date
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Currency
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Amount
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        GBP Value
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider">
                                                        Related Invoices
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider">
                                                        Related Projects
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider">
                                                        Invoiced %
                                                    </th>
                                                    <th scope="col" class="relative px-6 py-3">
                                                        <span class="sr-only">Edit</span>
                                                    </th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                <tr v-if="supplier_invoices.data.length > 0"
                                                    v-for="(supplier_invoice, index) in supplier_invoices.data" :key="index"
                                                    :class="{'bg-gray-100': index % 2}"
                                                    class="hover:bg-gray-50 cursor-pointer">
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {{ supplier_invoice.invoice_number }}
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {{ supplier_invoice.supplier.name }}
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {{ moment(supplier_invoice.received_at, 'YYYY-MM-DD').format('DD-MM-YYYY') }}
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {{ supplier_invoice.currency.name }}
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {{ supplier_invoice.currency.symbol}}{{ number_format(parseFloat(supplier_invoice.amount).toFixed(2)) }}
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        &pound;{{ number_format(parseFloat(supplier_invoice.gbp_amount).toFixed(2)) }}
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        <div class="flex justify-center items-center gap-3">
                                                            <span class="underline text-pitch-purple text-gray-500"
                                                              @click="emitter.emit('openLinkSupplierInvoiceSlideout', supplier_invoice)">{{
                                                                supplier_invoice.related_invoices.length
                                                            }}</span>
                                                            <button @click="emitter.emit('openLinkSupplierInvoiceSlideout', supplier_invoice)" type="button"
                                                                    class="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-amaze-coral hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                                <PlusSmIcon class="h-4 w-4 text-white" aria-hidden="true"/>
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        <div class="flex justify-center items-center gap-3">
                                                            <span class="underline text-pitch-purple text-gray-500"
                                                              @click="emitter.emit('openLinkSupplierInvoiceProjectSlideout', supplier_invoice)">{{
                                                                supplier_invoice.related_projects.length
                                                            }}</span>
                                                            <button @click="emitter.emit('openLinkSupplierInvoiceProjectSlideout', supplier_invoice)" type="button"
                                                                    class="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-amaze-coral hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                                <PlusSmIcon class="h-4 w-4 text-white" aria-hidden="true"/>
                                                            </button>
                                                            <Popper v-if="parseFloat(supplier_invoice.gbp_amount) !== parseFloat(supplier_invoice.total_project_linked_amount)" :hover="true" class="cursor-pointer" placement="top" content="The total invoice amount has not been assigned across projects.">
                                                                <ExclamationCircleIcon
                                                                class="h-5 w-5 text-red-500"
                                                                aria-hidden="true"/>
                                                            </Popper>
                                                        </div>
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                                                        <span class="flex items-center gap-2">

                                                        {{ supplier_invoice.invoiced_amount }}%
                                                        <CheckCircleIcon v-if="supplier_invoice.invoiced_amount <= 100" alt="Remove Folder"
                                                                          class="cursor-pointer w-4 h-4 text-green-400 hover:text-gray-500"></CheckCircleIcon>
                                                        <Popper v-else :hover="true" class="cursor-pointer" placement="top" content="The amount raised is over the expected amount for this supplier invoice, please check the invoices raised for this">
                                                        <XCircleIcon alt="Remove Folder"
                                                                          class="cursor-pointer w-4 h-4 text-red-400 hover:text-gray-500"></XCircleIcon>
                                                        </Popper>
                                                                                                                    </span>

                                                    </td>
                                                    <td class="px-6  py-4 whitespace-nowrap text-right text-sm flex">
                                                        <a :href="supplier_invoice.mail_to"><MailIcon
                                                                       class="w-4 h-4 mr-3"></MailIcon></a>
                                                        <PencilAltIcon @click="emitter.emit('editSupplierInvoiceSlideout', supplier_invoice)"
                                                                       class="w-4 h-4 mr-3"></PencilAltIcon>
                                                        <TrashIcon @click="deleteSupplierInvoice(supplier_invoice.id)"
                                                                   class="w-4 h-4 text-red-500"></TrashIcon>
                                                    </td>
                                                </tr>
                                                <tr v-else>
                                                    <td colspan="9"
                                                        class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">
                                                        There are no supplier invoices available for your search criteria.
                                                    </td>
                                                </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                                <Pagination :links="supplier_invoices.links"/>
                            </div>

                            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </section>
        </div>
    </div>
    <CreateSupplierInvoice/>
    <LinkExistingSupplierInvoice/>
    <LinkSupplierInvoiceToInvoice/>
    <LinkSupplierInvoiceToProject/>
</template>


<script>
import Authenticated from "@/Layouts/Authenticated.vue";
import {Link, Head} from "@inertiajs/vue3";
import {
    ClipboardCopyIcon,
    PlusSmIcon,
    UsersIcon,
    InformationCircleIcon,
    ChevronDownIcon,
    CogIcon,
    PencilAltIcon,
    CheckCircleIcon,
    TrashIcon,
    MailIcon,
    XCircleIcon,
    ExclamationCircleIcon
} from "@heroicons/vue/outline"
import ValidationErrors from "@/Components/ValidationErrors";
import ProjectHeader from "@/Components/ProjectHeader";
import CreateQuote from "@/Components/SlideOuts/CreateQuote";
import {Popover, PopoverButton, PopoverGroup} from "@headlessui/vue";
import Button from "@/Components/Button.vue";

import DuplicateQuote from "@/Components/SlideOuts/DuplicateQuote";
import Pagination from "@/Components/Pagination.vue";
import CreateExpense from "@/Components/SlideOuts/CreateExpense.vue";
import moment from "moment";
import {emitter} from "@/eventHub";
import CreateSupplierInvoice from "@/Components/SlideOuts/CreateSupplierInvoice";
import LinkExistingSupplierInvoice from "@/Components/SlideOuts/LinkExistingSupplierInvoice";
import LinkSupplierInvoiceToInvoice from "@/Components/SlideOuts/LinkSupplierInvoiceToInvoice";
import LinkSupplierInvoiceToProject from "@/Components/SlideOuts/LinkSupplierInvoiceToProject";
import Popper from "vue3-popper";

export default {
    layout: Authenticated,
    components: {
        LinkSupplierInvoiceToInvoice,
        LinkSupplierInvoiceToProject,
        CreateSupplierInvoice,
        LinkExistingSupplierInvoice,
        CreateExpense,
        DuplicateQuote,
        Button,
        CreateQuote,
        ProjectHeader,
        ValidationErrors,
        Link,
        Head,
        PlusSmIcon,
        UsersIcon,
        InformationCircleIcon,
        Popover,
        PopoverButton,
        ChevronDownIcon,
        PopoverGroup,
        CogIcon,
        PencilAltIcon,
        ClipboardCopyIcon,
        CheckCircleIcon,
        Pagination,
        TrashIcon,
        MailIcon,
        XCircleIcon,
        Popper,
        ExclamationCircleIcon
    },
    props: {
        project: Object,
        supplier_invoices: Object,
    },
    data() {
        return {
            showErrors: false,
            emitter: emitter
        }
    },
    created() {
        this.moment = moment
    },
    methods: {
        deleteSupplierInvoice(id) {
            this.$swal.fire({
                title: 'Remove Supplier Invoice',
                html: "Removing this supplier invoice will delete this entry and any asscoiations made to customer invoices attributed to this supplier invoice. Once this has been removed the information will no longer be available on the project.<br/><br/>" +
                    "Are you sure you wish to do this?",
                showCloseButton: true,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No'
            })
                .then((result) => {
                    if (result.isConfirmed) {
                        // Delete the country
                        Inertia.delete(route('frontend.project.supplier-invoice.invoice.delete', [this.project.id, id]), {
                            preserveScroll: true
                        });
                    }
                });
        },
        number_format(nStr) {
            nStr += '';
            let x = nStr.split('.');
            let x1 = x[0];
            let x2 = x.length > 1 ? '.' + x[1] : '';
            let rgx = /(\d+)(\d{3})/;
            while (rgx.test(x1)) {
                x1 = x1.replace(rgx, '$1' + ',' + '$2');
            }
            return x1 + x2;
        },
    }
}
</script>
